





























import CursorDiagramToolbar from '@c/toolbar/CursorDiagramToolbar.vue';
import {Component, Vue, Prop} from 'vue-property-decorator';
import {Chart} from 'highcharts-vue';
import {getFormattedDate, getFormattedDateWithCondition, getDurationStr} from '@u/dateTimeUtils';
import {CursorDiagramModeManager} from '@/logic/cursorDiagramModeManager';
import {CursorLabelModel} from '@/logic/cursors/labelModels/cursorLabelModel';
import {MarkerCursorLabelModel} from '@/logic/cursors/labelModels/markerCursorLabelModel';
import {signalDiagramModes} from '@/logic/diagramModes';
import {uuidv4} from '@u/uuidUtils';
import {CursorDiagramState} from '@/logic/cursorDiagramState';
import {DiagramModeType, DiagramType} from '@/enums/diagrams';
import {FindPeaksAction} from '@/logic/diagramActions/findPeaksAction';
import {FindPeaksLabelModel} from '@/logic/diagramActions/labelModels/findPeaksLabelModel';
import {DiagramData, DiagramMode, DiagramSettings} from '@/interfaces/diagrams';
import DiagramSettingsDialog from '@/components/diagrams/nodeConfigs/DiagramSettingsDialog.vue';
import {DiagramAxisProvider} from '@/logic/diagramAxisProvider';
import {signalColorScheme} from '@/utils/colorSchemes';

@Component({
  components: {
    highcharts: Chart,
    CursorDiagramToolbar,
    DiagramSettingsDialog,
  },
})
export default class CoolantDiagram extends Vue {
  // Настройки диаграммы
  settings: DiagramSettings = {
    isYLogAxisShow: true,
    isYLogAxis: false,
    hideZeroFrequency: false,
    type: DiagramType.Signal,
    matchColor: signalColorScheme[0].mainColor,
  };

  // Идентификатор диаграммы
  id: string = uuidv4();

  // Режимы диаграммы
  diagramModes: DiagramMode[] = signalDiagramModes;

  // Предустановленные режимы
  preselectedModes: DiagramMode[] = [
    this.diagramModes.find(mode => mode.type === DiagramModeType.View) as DiagramMode,
  ];

  // Провайдер для работы с осями
  diagramAxisProvider: DiagramAxisProvider = new DiagramAxisProvider();

  // Менеджер для работы с диаграммой
  modeManager: CursorDiagramModeManager = new CursorDiagramModeManager(
    DiagramType.Signal,
    new CursorLabelModel('', this.xCursorLabelFormatter),
    new MarkerCursorLabelModel('', this.xCursorLabelFormatter, this.xMarkerBandLabelFormatter),
    null,
    [new FindPeaksAction(new FindPeaksLabelModel('', this.xCursorLabelFormatter))],
    (() => {
      const state = new CursorDiagramState();
      state.diagramId = this.id;
      state.activeModes = this.preselectedModes;
      return state;
    })(),
  );

  // Данные для графика
  @Prop({required: true, default: {}})
  chartData!: DiagramData;

  // Скрытый интервал, необходим для отображения графика без данных
  @Prop({required: true, type: Array})
  extendChartData!: [];

  // Данные полигонов (статусы работы)
  @Prop({required: true, type: Array})
  plotBands!: [];

  // Данный линий (границы полигонов - статусов работы)
  @Prop({required: true, type: Array})
  plotLines!: [];

  // Минимальное значение масштаба по оси X
  @Prop({type: Number, default: 0})
  xMinScaleValue!: number;

  isInZoom = false;

  // Область графика
  extremesX: any;

  extremesY: any;

  // Идентификатор основной серии графика (нужно знать для подключения курсоров)
  mainSeriesId = `mainSeries-${this.id}`;

  // Показатель активности тулбара для работы с графиком
  get toolbarDisabled() {
    return !this.chartData.data.length && !this.plotBands.length;
  }

  setSettings(newSettings: DiagramSettings) {
    this.settings = {...newSettings};
  }

  /* Обработчики кнопок тулбара */

  /*
    Выбор режима графика
  */
  selectModes(modes: DiagramMode[]) {
    this.modeManager.updateState([...modes]);
  }
  /* */

  mounted() {
    const chart = this.getChart();
    this.extremesX = chart.xAxis[0].getExtremes();
    this.extremesY = chart.yAxis[0].getExtremes();
    this.modeManager.init(chart, [this.mainSeriesId], this.xMinScaleValue);
  }

  /*
    Действия при изменении статуса отображения линии курсора
  */
  changeShowCursorLine(show: boolean) {
    this.modeManager.showLine(show);
  }

  /*
    Действия при изменении статуса отображения отметок курсора
  */
  changeShowCursorTooltips(show: boolean) {
    this.modeManager.showTooltips(show);
  }

  /*
    Форматтер для курсора
  */
  xCursorLabelFormatter(value: any) {
    return getFormattedDate(value, 'HH:mm:ss (dd.MM)');
  }

  /*
    Форматтер для маркера
  */
  xMarkerBandLabelFormatter(value: any) {
    return getDurationStr(value);
  }

  /*
    Объект конфигурации графика.
    Computed свойства для подстановки в plotOptions использовать нельзя
    все обновления по возможности нужно делать через chart.update
  */
  get chartOptions() {
    const yAxisData = this.diagramAxisProvider.getYAxis(
      {mainDiagram: this.chartData, comparedDiagrams: []},
      signalColorScheme,
      this.settings.isYLogAxis,
    );
    const yAxis = yAxisData.axis;

    return {
      credits: {
        enabled: false,
      },
      title: '',
      xAxis: {
        gridLineWidth: 1,
        labels: {
          formatter: (e: any) => {
            const extremes = e.chart.xAxis[0].getExtremes();
            const diff = extremes.max - extremes.min;
            return getFormattedDateWithCondition(e.value, diff);
          },
        },
        title: {
          text: 'Время',
        },
        plotBands: this.plotBands,
        plotLines: this.plotLines,
      },
      yAxis: yAxis,
      series: [
        {
          id: this.mainSeriesId,
          data: this.chartData.data,
          type: 'line',
          findNearestPointBy: 'xy',
          yAxis: 0,
          color: this.settings.matchColor,
        },
        {
          data: this.extendChartData,
          color: 'rgba(0,0,0,0)',
          enableMouseTracking: false,
          showInLegend: false,
        },
      ],
      navigation: {
        buttonOptions: {
          enabled: false,
        },
      },
      plotOptions: {
        series: {
          animation: {
            duration: 0,
          },
          states: {
            hover: {
              enabled: false,
              halo: {
                size: 0,
              },
            },
            inactive: {
              opacity: 1,
            },
          },
        },
        line: {
          turboThreshold: 0,
          boostThreshold: 0,
          animation: {
            duration: 0,
          },
          marker: {
            enabled: false,
            radius: 0,
          },
        },
      },
      chart: {
        animation: false,
        events: {
          selection: this.onSelection,
          render: this.onRender,
          afterUpdate: this.afterUpdate,
        },
        zoomType: this.modeManager.zoomType,
      },
      legend: {
        enabled: false,
      },
      tooltip: {
        enabled: false,
        crosshairs: [false],
      },
    };
  }

  /*
     Действия после обновления графика - необходимо перерисовать
     все, что применено на график с режимов
     т. к. объект графика обновится
  */
  afterUpdate() {
    this.modeManager.updateActiveModes(this.modeManager.getState(), [this.mainSeriesId]);
  }

  /*
    Получить объект диаграммы
  */
  getChart() {
    return (this.$refs.chart as any).chart;
  }

  getToolbar() {
    return this.$refs.cursorDiagramToolbar as any;
  }

  /*
    Обработчик события 'render' библиотеки hightChart
  */
  onRender() {
    // Некоторые действия, например отрисовку отметок на диагамме нужно делать на onRender
    // т. к. требуется перерисовка на любое изменение графика (например изменение размера)
    this.modeManager.renderActiveModes();
    const chart = this.getChart();
    if (chart && chart.resetZoomButton) {
      chart.resetZoomButton.hide();
    }
  }

  /*
    Обработчик события 'selection' библиотеки hightChart
  */
  onSelection(e: any) {
    //отменяем стандартный обработчик события
    e.preventDefault();
    const dateInterval = this.modeManager.applyZoom(e.xAxis[0], e.yAxis[0]);
    if (dateInterval) {
      this.$emit('zoom', dateInterval);
      this.isInZoom = true;
    }
  }

  // Сброс зума
  skipZoom() {
    if (this.modeManager.skipZoom()) {
      this.$emit('skipZoom');
      this.isInZoom = false;
    }
  }

  // Отмена текущего зума
  undoZoom() {
    const result = this.modeManager.undoZoom();
    if (result === 'skip') {
      this.$emit('skipZoom');
      this.isInZoom = false;
    } else if (result) this.$emit('zoom', result);
  }
}
